/**
 *
 * ChallengeQuestionForm Component
 *
 */

import {
  Button,
  Enums,
  stylize,
  TextField,
} from "@envestnet/envreact-component-library";
import classnames from "classnames";
import Exclamation from "images/Exclamation";
import React, { useEffect } from "react";
import checkEmailValidation from "utils/emailValidator";
import withIntl from "utils/withIntl";
import messages from "./messages";
import SkeletonLoader from "./skeletonLoader";
import styles, { Styles } from "./styles";

const { ButtonSize, ButtonStyle } = Enums;

interface InjectedProps extends Styles {
  /** Internal - injected from withIntl */
  formatMessage: FormatMessage;

  /** Internal - injected from WithStyles */
  innerRef?: React.RefObject<HTMLDivElement>;
}

interface DefaultProps {
  // Default props should always be required props, i.e. no ? indicating it's optional
  // The default values are specified below

  /** Description of this prop */
  question: string;
  loading: boolean;
  hasEmailError: boolean;
  noOfRetries: number;
}

export interface ChallengeQuestionFormProps extends DefaultProps {
  /** CSS class name to be applied to top level component */
  className?: string;

  /** Inline style object to be applied to top level component */
  style?: React.CSSProperties;

  /** Description of this prop */
  onSubmit: Function;
}

const defaultProps: DefaultProps = {
  question: "No challenge question configured",
  loading: false,
  hasEmailError: false,
  noOfRetries: 3,
};

type Props = InjectedProps & ChallengeQuestionFormProps;

const ChallengeQuestionForm = (props: Props) => {
  const {
    classes,
    style,
    className,
    formatMessage,
    innerRef,
    question,
    onSubmit,
    loading,
    hasEmailError,
    noOfRetries,
  } = props;

  const [ca, setCa] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [emailErr, setEmailError] = React.useState<string | null>(null);

  useEffect(() => {
    if (hasEmailError) setEmailError(formatMessage(messages.emailErrorHint));
  }, [hasEmailError]);

  if (!question) {
    return <SkeletonLoader />;
  }
  const isQuestionConfigured = question !== "No challenge question configured";

  return (
    <div
      ref={innerRef}
      className={classnames(className, classes.container)}
      style={style}
    >
      <div className={classnames(classes.welcomeText, "header-font-family")}>
        {formatMessage(messages.header)}
      </div>
      <div
        className={classnames(
          classes.createAccountText,
          "header-text-font-family"
        )}
      >
        {formatMessage(messages.createYourAccount)}
      </div>
      <div data-testid="challenge-question-emailTextField">
        <TextField
          label={formatMessage(messages.emailLabel)}
          text={email}
          textStyle={{ fontFamily: "Red Hat Text" }}
          onChangeText={(input) => {
            if (!checkEmailValidation(input)) {
              setEmailError(formatMessage(messages.emailErrorHint));
            } else {
              setEmailError(null);
            }
            setEmail(input);
          }}
          errorMessage={emailErr}
          className={classnames(
            classes.textfield,
            "no-border-radius",
            "header-font-family"
          )}
          aria-label="challenge question email TextField"
        />
      </div>
      {isQuestionConfigured ? (
        <div data-testid="challenge-question-answerTextField">
          <TextField
            label={question}
            text={ca}
            textStyle={{ fontFamily: "Red Hat Text" }}
            onChangeText={(text) => setCa(text)}
            className={classnames(
              classes.textfield,
              "no-border-radius",
              "header-font-family"
            )}
            aria-label="challenge question answer TextField"
          />
        </div>
      ) : null}
      {noOfRetries > 0 && noOfRetries < 3 && (
        <div
          className={classnames(
            classes.errorContainer,
            "header-text-font-family"
          )}
          data-testid="error-container"
        >
          <Exclamation />
          <span>
            {formatMessage(messages[`errorChallengeQuestion${noOfRetries}`])}
          </span>
        </div>
      )}
      <div data-testid="challenge-question-submitButton">
        <Button
          buttonStyle={ButtonStyle.Primary}
          buttonSize={ButtonSize.Regular}
          className={classnames(
            classes.submitButton,
            "no-border-radius",
            "header-text-font-family"
          )}
          name={formatMessage(messages.submitButtonLabel)}
          onClick={() => {
            onSubmit({ email, answer: ca });
          }}
          loading={loading}
          disabled={!email || !!emailErr || (isQuestionConfigured && !ca)}
          aria-label="challenge question submit button"
        />
      </div>
    </div>
  );
};

ChallengeQuestionForm.defaultProps = defaultProps;

export default withIntl(
  stylize<Props, DefaultProps>(styles)(ChallengeQuestionForm)
);
